/**
 * Constants for the Condition Builder component.
 * Defines available fields, operators, and their configurations.
 */

import i18n from "@tellescope/i18n";
import type { DataSourceType } from "../../../shared/schema/visibility";

// Field type definitions
export type FieldType = "string" | "number" | "array" | "date" | "boolean";

// Field option for predefined value dropdowns
export interface FieldOption {
  value: string;
  label: string;
}

// Field definition
export interface FieldDefinition {
  id: string;
  label: string;
  type: FieldType;
  description?: string;
  /** If set, only these operators are available for this field */
  allowedOperators?: string[];
  /** If set, value input becomes a dropdown with these options */
  options?: FieldOption[];
  /**
   * If set, this field requires a suffix key input.
   * The final field path will be `{id}.{userEnteredKey}`.
   * Used for metadata fields where the user specifies the key name.
   */
  suffixKeyInput?: {
    /** Placeholder text for the key input */
    placeholder: string;
  };
}

// Operator definition
export interface OperatorDefinition {
  id: string;
  label: string;
  requiresValue: boolean;
  valueType?: "text" | "number" | "select" | "multiselect" | "date" | "boolean";
}

// Data source definition
export interface DataSourceDefinition {
  id: DataSourceType;
  label: string;
  singularLabel: string;
  description: string;
  fields: FieldDefinition[];
}

/**
 * Enduser fields available for visibility conditions.
 * Phase 1: Only enduser fields are supported.
 */
export const ENDUSER_FIELDS: FieldDefinition[] = [
  // Basic profile
  { id: "fname", label: "First Name", type: "string" },
  { id: "lname", label: "Last Name", type: "string" },
  { id: "email", label: "Email", type: "string" },
  { id: "phone", label: "Phone", type: "string" },
  { id: "dateOfBirth", label: "Date of Birth", type: "date" },
  { id: "gender", label: "Gender", type: "string" },

  // Location
  { id: "state", label: "State", type: "string" },
  { id: "city", label: "City", type: "string" },
  { id: "zipCode", label: "Zip Code", type: "string" },
  { id: "addressLineOne", label: "Address Line 1", type: "string" },
  { id: "addressLineTwo", label: "Address Line 2", type: "string" },

  // Arrays
  { id: "tags", label: "Tags", type: "array", description: "Patient tags" },
  { id: "assignedTo", label: "Assigned To", type: "array", description: "Assigned user IDs" },
  { id: "$statuses", label: "Journey Status", type: "array", description: "Patient journey statuses" },

  // Nested fields
  { id: "insurance.payerName", label: "Insurance Payer", type: "string" },
  { id: "insurance.memberId", label: "Insurance Member ID", type: "string" },
  { id: "insurance.relationship", label: "Insurance Relationship", type: "string" },

  // IDs
  { id: "customTypeId", label: "Contact Type", type: "string", description: "Contact type ID" },
  { id: "journeyId", label: "Journey", type: "array", description: "Journey IDs" },
];

/**
 * Form response fields available for visibility conditions.
 * Uses "any match" semantics - condition is true if ANY form response matches.
 */
export const FORM_RESPONSE_FIELDS: FieldDefinition[] = [
  {
    id: "formTitle",
    label: "Form Title",
    type: "string",
    description: "Title of the submitted form",
  },
  {
    id: "submittedAt",
    label: "Submitted Date",
    type: "date",
    description: "Date when the form was submitted (null if not yet submitted)",
  },
];

/**
 * Stripe subscription status options.
 * Based on Stripe API subscription status values.
 */
export const STRIPE_SUBSCRIPTION_STATUS_OPTIONS: FieldOption[] = [
  { value: "active", label: "Active" },
  { value: "canceled", label: "Canceled" },
  { value: "incomplete", label: "Incomplete" },
  { value: "incomplete_expired", label: "Incomplete Expired" },
  { value: "past_due", label: "Past Due" },
  { value: "paused", label: "Paused" },
  { value: "trialing", label: "Trialing" },
  { value: "unpaid", label: "Unpaid" },
];

/**
 * Stripe subscription fields available for visibility conditions.
 * Uses "any match" semantics - condition is true if ANY subscription matches.
 */
export const STRIPE_SUBSCRIPTION_FIELDS: FieldDefinition[] = [
  {
    id: "status",
    label: "Status",
    type: "string",
    description: "Subscription status",
    allowedOperators: ["$eq"],
    options: STRIPE_SUBSCRIPTION_STATUS_OPTIONS,
  },
  {
    id: "productName",
    label: "Product Name",
    type: "string",
    description: "Name of the subscribed product",
  },
  {
    id: "productMetadata",
    label: "Product Metadata",
    type: "string",
    description: "Product metadata key-value pair from Stripe",
    suffixKeyInput: {
      placeholder: "e.g., tier, plan_type",
    },
  },
];

const createdAtField: FieldDefinition = {
  id: "createdAt",
  label: "Created At",
  type: "date",
  description: "Creation timestamp",
};
const updatedAtField: FieldDefinition = {
  id: "updatedAt",
  label: "Updated At",
  type: "date",
  description: "Last update timestamp",
};
const sourceField: FieldDefinition = {
  id: "source",
  label: "Source",
  type: "string",
  description: "Source",
};

/**
 * Form fields available for visibility conditions.
 * Used for item filtering in data blocks (e.g., filtering pinned forms).
 * Represents the current form being evaluated.
 */
export const FORM_FIELDS: FieldDefinition[] = [
  {
    id: "title",
    label: "Title",
    type: "string",
    description: "Form title",
  },
  {
    id: "displayTitle",
    label: "Display Title",
    type: "string",
    description: "Display title (if set)",
  },
  createdAtField,
  updatedAtField,
];

/**
 * Appointment status options derived from EventStatus interface.
 */
export const APPOINTMENT_STATUS_OPTIONS: FieldOption[] = [
  { value: "upcoming", label: i18n.t("Upcoming", { ns: "common" }) },
  { value: "past", label: i18n.t("Past", { ns: "common" }) },
  { value: "cancelled", label: i18n.t("Cancelled", { ns: "common" }) },
  { value: "rescheduled", label: i18n.t("Rescheduled", { ns: "common" }) },
];

/**
 * Appointment (CalendarEvent) fields for item filtering.
 */
export const APPOINTMENT_FIELDS: FieldDefinition[] = [
  {
    id: "title",
    label: "Title",
    type: "string",
    description: "Event title",
  },
  {
    id: "calendarEventTemplateId",
    label: "Template ID",
    type: "string",
    description: "Calendar event template ID",
  },
  {
    id: "startTimeInMS",
    label: "Start Timestamp",
    type: "date",
    description: "Event start time",
  },
  {
    id: "$status",
    label: "Status",
    type: "string",
    description: "Computed event status",
    allowedOperators: ["$eq"],
    options: APPOINTMENT_STATUS_OPTIONS,
  },
  {
    id: "cancelledAt",
    label: "Cancelled At",
    type: "date",
    description: "Cancellation timestamp (if cancelled)",
  },
  {
    id: "rescheduledAt",
    label: "Rescheduled At",
    type: "date",
    description: "Rescheduled timestamp (if rescheduled)",
  },
  createdAtField,
  updatedAtField,
  sourceField,
];

/**
 * Order (EnduserOrder) fields for item filtering.
 */
export const ORDER_FIELDS: FieldDefinition[] = [
  {
    id: "title",
    label: "Title",
    type: "string",
    description: "Order title",
  },
  {
    id: "status",
    label: "Status",
    type: "string",
    description: "Order status",
  },
  createdAtField,
  updatedAtField,
  sourceField,
];

/**
 * ChatRoom fields for item filtering.
 */
export const CHAT_ROOM_FIELDS: FieldDefinition[] = [
  {
    id: "title",
    label: "Title",
    type: "string",
    description: "Chat room title",
  },
  createdAtField,
  updatedAtField,
  sourceField,
];

/**
 * File fields for item filtering.
 */
export const FILE_FIELDS: FieldDefinition[] = [
  {
    id: "name",
    label: "Name",
    type: "string",
    description: "File name",
  },
  {
    id: "type",
    label: "MIME Type",
    type: "string",
    description: "File MIME type (e.g., image/png, application/pdf)",
  },
  createdAtField,
  updatedAtField,
  sourceField,
];

/**
 * Content (ManagedContentRecord) fields for item filtering.
 */
export const CONTENT_FIELDS: FieldDefinition[] = [
  {
    id: "title",
    label: "Title",
    type: "string",
    description: "Content title",
  },
  {
    id: "type",
    label: "Type",
    type: "string",
    description: "Content type (Article, Video, PDF)",
  },
  {
    id: "category",
    label: "Category",
    type: "string",
    description: "Content category",
  },
  createdAtField,
  updatedAtField,
  sourceField,
];

/**
 * Purchase fields for item filtering.
 */
export const PURCHASE_FIELDS: FieldDefinition[] = [
  {
    id: "title",
    label: "Title",
    type: "string",
    description: "Purchase title/description",
  },
  {
    id: "cost.amount",
    label: "Amount (in cents)",
    type: "number",
    description: "Purchase amount (in cents)",
  },
  {
    id: "processor",
    label: "Processor",
    type: "string",
    description: "Payment processor (e.g., stripe)",
  },
  {
    id: "description",
    label: "Description",
    type: "string",
    description: "Purchase description",
  },
  createdAtField,
  updatedAtField,
  sourceField,
];

/**
 * Appointment Booking Page fields for item filtering.
 */
export const APPOINTMENT_BOOKING_PAGE_FIELDS: FieldDefinition[] = [
  {
    id: "title",
    label: "Title",
    type: "string",
    description: "Booking page title",
  },
  createdAtField,
  updatedAtField,
];

/**
 * Operators available for each field type.
 */
export const OPERATORS_BY_TYPE: Record<FieldType, OperatorDefinition[]> = {
  string: [
    { id: "$eq", label: "equals", requiresValue: true, valueType: "text" },
    { id: "$ne", label: "does not equal", requiresValue: true, valueType: "text" },
    { id: "$in", label: "is one of", requiresValue: true, valueType: "multiselect" },
    { id: "$nin", label: "is not one of", requiresValue: true, valueType: "multiselect" },
    { id: "$regex", label: "matches pattern", requiresValue: true, valueType: "text" },
    { id: "$exists", label: "exists", requiresValue: true, valueType: "boolean" },
  ],
  number: [
    { id: "$eq", label: "equals", requiresValue: true, valueType: "number" },
    { id: "$ne", label: "does not equal", requiresValue: true, valueType: "number" },
    { id: "$gt", label: "greater than", requiresValue: true, valueType: "number" },
    { id: "$gte", label: "greater than or equal", requiresValue: true, valueType: "number" },
    { id: "$lt", label: "less than", requiresValue: true, valueType: "number" },
    { id: "$lte", label: "less than or equal", requiresValue: true, valueType: "number" },
    { id: "$in", label: "is one of", requiresValue: true, valueType: "multiselect" },
    { id: "$nin", label: "is not one of", requiresValue: true, valueType: "multiselect" },
    { id: "$exists", label: "exists", requiresValue: true, valueType: "boolean" },
  ],
  array: [
    { id: "$in", label: "includes any of", requiresValue: true, valueType: "multiselect" },
    { id: "$nin", label: "does not include any of", requiresValue: true, valueType: "multiselect" },
    { id: "$exists", label: "is not empty", requiresValue: true, valueType: "boolean" },
  ],
  date: [
    { id: "$gt", label: "after", requiresValue: true, valueType: "date" },
    { id: "$gte", label: "on or after", requiresValue: true, valueType: "date" },
    { id: "$lt", label: "before", requiresValue: true, valueType: "date" },
    { id: "$lte", label: "on or before", requiresValue: true, valueType: "date" },
    { id: "$exists", label: "exists", requiresValue: true, valueType: "boolean" },
  ],
  boolean: [
    { id: "$eq", label: "is", requiresValue: true, valueType: "boolean" },
    { id: "$exists", label: "exists", requiresValue: true, valueType: "boolean" },
  ],
};

/**
 * Data source configurations.
 * Each source represents a data model that can be used for visibility conditions.
 *
 * To add a new source:
 * 1. Add the source ID to the SourceId type above
 * 2. Create a FIELDS array for the source (e.g., ORDER_FIELDS)
 * 3. Add the source configuration to DATA_SOURCES below
 */
export const DATA_SOURCES: DataSourceDefinition[] = [
  {
    id: "enduser",
    label: "Contact",
    singularLabel: "Contact",
    description: "Patient/Contact data",
    fields: ENDUSER_FIELDS,
  },
  {
    id: "formResponse",
    label: "Form Responses",
    singularLabel: "Form Response",
    description: "Form response data (matches if ANY form response matches)",
    fields: FORM_RESPONSE_FIELDS,
  },
  {
    id: "stripeSubscription",
    label: "Stripe Subscriptions",
    singularLabel: "Stripe Subscription",
    description: "Stripe subscription data (matches if ANY subscription matches)",
    fields: STRIPE_SUBSCRIPTION_FIELDS,
  },
  // Item filter sources - for filtering items within data blocks
  {
    id: "form",
    label: "Forms",
    singularLabel: "Form",
    description: "Form definition (for item filtering)",
    fields: FORM_FIELDS,
  },
  {
    id: "appointment",
    label: "Appointments",
    singularLabel: "Appointment",
    description: "Appointment data",
    fields: APPOINTMENT_FIELDS,
  },
  {
    id: "order",
    label: "Orders",
    singularLabel: "Order",
    description: "Order data",
    fields: ORDER_FIELDS,
  },
  {
    id: "chatRoom",
    label: "Chat Rooms",
    singularLabel: "Chat Room",
    description: "Chat room/message thread data",
    fields: CHAT_ROOM_FIELDS,
  },
  {
    id: "file",
    label: "Files",
    singularLabel: "File",
    description: "File data",
    fields: FILE_FIELDS,
  },
  {
    id: "content",
    label: "Content",
    singularLabel: "Content Item",
    description: "Managed content record data",
    fields: CONTENT_FIELDS,
  },
  {
    id: "purchase",
    label: "Purchases",
    singularLabel: "Purchase",
    description: "Purchase/payment data",
    fields: PURCHASE_FIELDS,
  },
  {
    id: "appointmentBookingPage",
    label: "Appointment Booking Pages",
    singularLabel: "Appointment Booking Page",
    description: "Booking page data (for item filtering)",
    fields: APPOINTMENT_BOOKING_PAGE_FIELDS,
  },
];

/**
 * Get the default source ID.
 */
export function getDefaultSourceId(): string {
  return DATA_SOURCES[0]?.id ?? "enduser";
}

/**
 * Get a data source by ID.
 */
export function getDataSource(sourceId: string): DataSourceDefinition | undefined {
  return DATA_SOURCES.find((s) => s.id === sourceId);
}

/**
 * Get fields for a given source.
 */
export function getFieldsForSource(sourceId: string): FieldDefinition[] {
  return getDataSource(sourceId)?.fields ?? [];
}

/**
 * Get field definition by ID for a given source.
 * Also handles fields with suffix keys (e.g., `productMetadata.tier` matches `productMetadata`).
 */
export function getFieldDefinition(sourceId: string, fieldId: string): FieldDefinition | undefined {
  const source = DATA_SOURCES.find((s) => s.id === sourceId);
  if (!source) return undefined;

  // First try exact match
  const exactMatch = source.fields.find((f) => f.id === fieldId);
  if (exactMatch) return exactMatch;

  // Check if this is a field with a suffix key (e.g., `productMetadata.tier`)
  const baseFieldId = getBaseFieldId(fieldId);
  if (baseFieldId !== fieldId) {
    const baseField = source.fields.find((f) => f.id === baseFieldId);
    // Only return if the base field supports suffix keys
    if (baseField?.suffixKeyInput) return baseField;
  }

  return undefined;
}

/**
 * Gets the base field ID from a field that may have a suffix key.
 * e.g., `productMetadata.tier` -> `productMetadata`
 * e.g., `productName` -> `productName` (no change)
 */
export function getBaseFieldId(fieldId: string): string {
  const dotIndex = fieldId.indexOf(".");
  return dotIndex > 0 ? fieldId.slice(0, dotIndex) : fieldId;
}

/**
 * Gets the suffix key from a field ID, if present.
 * e.g., `productMetadata.tier` -> `tier`
 * e.g., `productName` -> `""` (empty string)
 */
export function getSuffixKey(fieldId: string): string {
  const dotIndex = fieldId.indexOf(".");
  return dotIndex > 0 ? fieldId.slice(dotIndex + 1) : "";
}

/**
 * Creates a field ID with a suffix key.
 * e.g., `productMetadata` + `tier` -> `productMetadata.tier`
 */
export function createFieldIdWithSuffix(baseFieldId: string, suffixKey: string): string {
  return suffixKey ? `${baseFieldId}.${suffixKey}` : baseFieldId;
}

/**
 * Get operators for a field type.
 */
export function getOperatorsForType(type: FieldType): OperatorDefinition[] {
  return OPERATORS_BY_TYPE[type] ?? [];
}

/**
 * Get operators for a field, respecting field-level restrictions.
 * If fieldDef has allowedOperators, filters to only those.
 * Otherwise returns all operators for the field type.
 */
export function getOperatorsForField(fieldDef: FieldDefinition | undefined): OperatorDefinition[] {
  if (!fieldDef) return [];

  const allOperators = OPERATORS_BY_TYPE[fieldDef.type] ?? [];

  // If field has operator restrictions, filter to allowed ones
  if (fieldDef.allowedOperators && fieldDef.allowedOperators.length > 0) {
    return allOperators.filter((op) => fieldDef.allowedOperators!.includes(op.id));
  }

  return allOperators;
}

/**
 * Get operator definition by ID for a given field type.
 */
export function getOperatorDefinition(type: FieldType, operatorId: string): OperatorDefinition | undefined {
  return getOperatorsForType(type).find((op) => op.id === operatorId);
}

/**
 * Data sources that are array-based (support $elemMatch per-item evaluation).
 * Excludes singular sources like "enduser" and "form".
 */
export const ARRAY_DATA_SOURCES: DataSourceType[] = [
  "formResponse",
  "stripeSubscription",
  "appointment",
  "appointmentBookingPage",
  "order",
  "chatRoom",
  "file",
  "content",
  "purchase",
];
